'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface SignUpFormValues {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

// Google SVG icon component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.3441 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9574C.3477 6.1731 0 7.5477 0 9s.3477 2.8268.9574 4.0418L3.964 10.71z" fill="#FBBC05"/>
    <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4627.8918 11.4255 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z" fill="#EA4335"/>
  </svg>
);

export default function SignUpForm({ onSwitchToLogin }: Props) {
  const { signUp, signInWithGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false },
  });

  const passwordValue = watch('password');

  const getPasswordStrength = (pw: string): { label: string; color: string; width: string } => {
    if (!pw) return { label: '', color: 'bg-border', width: 'w-0' };
    if (pw.length < 6) return { label: 'Too short', color: 'bg-danger', width: 'w-1/4' };
    if (pw.length < 8) return { label: 'Weak', color: 'bg-warning', width: 'w-1/2' };
    if (/[A-Z]/.test(pw) && /[0-9]/.test(pw)) return { label: 'Strong', color: 'bg-success', width: 'w-full' };
    return { label: 'Fair', color: 'bg-accent', width: 'w-3/4' };
  };

  const strength = getPasswordStrength(passwordValue);

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    setAuthError('');
    try {
      await signInWithGoogle();
      // Redirect is handled by Supabase OAuth flow
    } catch (error: any) {
      setAuthError(error?.message || 'Google sign-in failed. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const onSubmit = async (data: SignUpFormValues) => {
    setIsLoading(true);
    setAuthError('');
    try {
      const result = await signUp(data.email, data.password, { fullName: data.fullName });
      setIsLoading(false);
      if (result.needsConfirmation) {
        setNeedsEmailConfirmation(true);
      } else {
        setSuccess(true);
        window.location.href = '/dashboard';
      }
    } catch (error: any) {
      setAuthError(error?.message || 'Failed to create account. Please try again.');
      setIsLoading(false);
    }
  };

  if (needsEmailConfirmation) {
    return (
      <div className="text-center py-8 animate-scale-in">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: 'var(--primary-bg, #eff6ff)' }}>
          <Mail size={32} className="text-primary" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">Check your email</h3>
        <p className="text-muted-foreground text-sm mb-6">
          We sent a confirmation link to your email address. Click it to activate your account and sign in.
        </p>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="btn-primary px-6 py-2.5 text-sm"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="text-center py-8 animate-scale-in">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: 'var(--success-bg)' }}>
          <CheckCircle2 size={32} className="text-success" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">Account created!</h3>
        <p className="text-muted-foreground text-sm">Taking you to your dashboard…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground">Create your account</h2>
        <p className="text-muted-foreground text-sm mt-1.5">
          Free forever. No credit card required.
        </p>
      </div>

      {/* Auth error */}
      {authError && (
        <div className="flex items-start gap-3 p-3.5 rounded-lg border text-sm animate-slide-up mb-5"
          style={{ backgroundColor: 'var(--danger-bg)', borderColor: 'var(--danger)' }}>
          <AlertCircle size={16} className="text-danger flex-shrink-0 mt-0.5" />
          <span className="text-danger">{authError}</span>
        </div>
      )}

      {/* Google Sign Up */}
      <button
        type="button"
        onClick={handleGoogleSignUp}
        disabled={isGoogleLoading || isLoading}
        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors text-sm font-medium text-foreground disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isGoogleLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        Continue with Google
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">or sign up with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Full name */}
        <div>
          <label htmlFor="signup-name" className="form-label">Full name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Maya Chen"
            className={`form-input ${errors.fullName ? 'error' : ''}`}
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' },
            })}
          />
          {errors.fullName && (
            <p className="form-error"><AlertCircle size={12} />{errors.fullName.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="form-label">Email address</label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`form-input ${errors.email ? 'error' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="form-error"><AlertCircle size={12} />{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="form-label">Password</label>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              className={`form-input pr-10 ${errors.password ? 'error' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {passwordValue && (
            <div className="mt-2">
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{strength.label}</p>
            </div>
          )}
          {errors.password && (
            <p className="form-error"><AlertCircle size={12} />{errors.password.message}</p>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="signup-confirm" className="form-label">Confirm password</label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat your password"
              className={`form-input pr-10 ${errors.confirmPassword ? 'error' : ''}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val) => val === passwordValue || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="form-error"><AlertCircle size={12} />{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Terms */}
        <div>
          <div className="flex items-start gap-2.5">
            <input
              id="agree-terms"
              type="checkbox"
              className="w-4 h-4 rounded border-border accent-primary mt-0.5 flex-shrink-0"
              {...register('agreeToTerms', {
                required: 'You must agree to the terms to continue',
              })}
            />
            <label htmlFor="agree-terms" className="text-sm text-muted-foreground cursor-pointer select-none leading-relaxed">
              I agree to the{' '}
              <span className="text-primary font-medium hover:underline cursor-pointer">Terms of Service</span>
              {' '}and{' '}
              <span className="text-primary font-medium hover:underline cursor-pointer">Privacy Policy</span>
            </label>
          </div>
          {errors.agreeToTerms && (
            <p className="form-error mt-1.5"><AlertCircle size={12} />{errors.agreeToTerms.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || isGoogleLoading}
          className="btn-primary w-full py-3 text-base mt-2"
        >
          {isLoading ? (
            <><Loader2 size={16} className="animate-spin" />Creating account…</>
          ) : (
            'Create Free Account'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary font-semibold hover:underline"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}